import type { ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { SplashScreen } from '../components/SplashScreen.tsx';
import { RequireAuth } from '../lib/auth.tsx';
import { AppShell } from './AppShell.tsx';
import { NAV_ITEMS, type NavKey, type RouteHandle } from './nav.ts';
import { RootLayout } from './RootLayout.tsx';
import { RouteError } from './RouteError.tsx';

type PageModule = { default: ComponentType };

/** Page modules live in src/pages/<Name>/index.tsx and default-export their component. */
const lazyPage = (load: () => Promise<PageModule>) => async () => ({ Component: (await load()).default });

const MODULE_PAGES: Record<NavKey, () => Promise<PageModule>> = {
  dashboard: () => import('../pages/Dashboard/index.tsx'),
  inbox: () => import('../pages/Inbox/index.tsx'),
  registrations: () => import('../pages/Registrations/index.tsx'),
  registrationsCalendar: () => import('../pages/RegistrationsCalendar/index.tsx'),
  registrationsOverview: () => import('../pages/RegistrationsOverview/index.tsx'),
  registrationsKits: () => import('../pages/TeamKits/index.tsx'),
  campaigns: () => import('../pages/Campaigns/index.tsx'),
  schedule: () => import('../pages/Schedule/index.tsx'),
  deliveries: () => import('../pages/Deliveries/index.tsx'),
  retryCenter: () => import('../pages/RetryCenter/index.tsx'),
  contacts: () => import('../pages/Contacts/index.tsx'),
  savedReplies: () => import('../pages/SavedReplies/index.tsx'),
  teams: () => import('../pages/Teams/index.tsx'),
  events: () => import('../pages/Events/index.tsx'),
  payments: () => import('../pages/Payments/index.tsx'),
  folders: () => import('../pages/Folders/index.tsx'),
  related: () => import('../pages/Related/index.tsx'),
  segments: () => import('../pages/Segments/index.tsx'),
  // The rule-based bot replaced the AI settings page (owner, 22 Sep 2026); the old page code stays unused.
  ai: () => import('../pages/Bot/index.tsx'),
  notifications: () => import('../pages/Notifications/index.tsx'),
  reports: () => import('../pages/Reports/index.tsx'),
  connections: () => import('../pages/Connections/index.tsx'),
  slips: () => import('../pages/Slips/index.tsx'),
  telegram: () => import('../pages/Telegram/index.tsx'),
  imports: () => import('../pages/Imports/index.tsx'),
  users: () => import('../pages/Users/index.tsx'),
  settings: () => import('../pages/Settings/index.tsx'),
  audit: () => import('../pages/Audit/index.tsx'),
  systemHealth: () => import('../pages/SystemHealth/index.tsx'),
  venue: () => import('../pages/Venue/index.tsx'),
};

/**
 * Module routes match "/<module>/*": each page module owns its sub-routes
 * (e.g. contacts/:id, inbox/:conversationId) with descendant <Routes>, so
 * modules can be built independently without editing this file.
 */
const navRoutes: RouteObject[] = NAV_ITEMS.map((item): RouteObject => {
  const handle: RouteHandle = { title: item.label, group: item.group === item.label ? undefined : item.group, section: item.section, anyOf: item.anyOf, guideKey: item.key };
  const lazy = lazyPage(MODULE_PAGES[item.key]);
  return item.to === '/'
    ? { index: true, lazy, handle, ErrorBoundary: RouteError }
    : { path: `${item.to.slice(1)}/*`, lazy, handle, ErrorBoundary: RouteError };
});

const accountGroup = 'บัญชีของฉัน';

export const routes: RouteObject[] = [
  {
    id: 'root',
    element: <RootLayout />,
    // Shown while the first lazy route module loads.
    HydrateFallback: SplashScreen,
    ErrorBoundary: RouteError,
    children: [
      { path: 'login', lazy: lazyPage(() => import('../pages/Login/index.tsx')), ErrorBoundary: RouteError },
      // Public: Meta App Review and customers open it without an account.
      { path: 'privacy', lazy: lazyPage(() => import('../pages/Privacy/index.tsx')), ErrorBoundary: RouteError },
      // Meta requires a data-deletion URL that is not the privacy URL itself.
      { path: 'data-deletion', lazy: lazyPage(() => import('../pages/Privacy/DataDeletion.tsx')), ErrorBoundary: RouteError },
      // Public: the shop posts these links — "/teams" always shows the newest published competition.
      { path: 'teams', lazy: lazyPage(() => import('../pages/PublicTeams/index.tsx')), ErrorBoundary: RouteError },
      { path: 'teams/:slug', lazy: lazyPage(() => import('../pages/PublicTeams/index.tsx')), ErrorBoundary: RouteError },
      // Public: the link of ONE registration, sent to that team so it can keep its own facts and players (v1.93.0).
      { path: 't/:token', lazy: lazyPage(() => import('../pages/TeamKit/index.tsx')), ErrorBoundary: RouteError },
      { path: 'login/mfa', lazy: lazyPage(() => import('../pages/LoginMfa/index.tsx')), ErrorBoundary: RouteError },
      {
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        ErrorBoundary: RouteError,
        children: [
          ...navRoutes,
          {
            path: 'account/password',
            lazy: lazyPage(() => import('../pages/AccountPassword/index.tsx')),
            handle: { title: 'เปลี่ยนรหัสผ่าน', group: accountGroup } satisfies RouteHandle,
            ErrorBoundary: RouteError,
          },
          {
            path: 'account/security',
            lazy: lazyPage(() => import('../pages/AccountSecurity/index.tsx')),
            handle: { title: 'ความปลอดภัยบัญชี', group: accountGroup } satisfies RouteHandle,
            ErrorBoundary: RouteError,
          },
          {
            path: 'account/notifications',
            lazy: lazyPage(() => import('../pages/AccountNotifications/index.tsx')),
            handle: { title: 'การแจ้งเตือนแชท', group: accountGroup, guideKey: 'accountNotifications' } satisfies RouteHandle,
            ErrorBoundary: RouteError,
          },
          {
            path: '*',
            lazy: lazyPage(() => import('../pages/NotFound/index.tsx')),
            handle: { title: 'ไม่พบหน้า' } satisfies RouteHandle,
            ErrorBoundary: RouteError,
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
